import React, { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import {
  LayoutGrid, FileText, TrendingUp, Receipt, PhoneCall, Gauge, Scale, Banknote,
  Send, Sparkles, X, ShieldCheck, AlertTriangle, RefreshCw, Inbox, Search,
  CheckCircle2, XCircle, CalendarClock, ArrowUpRight, Repeat,
} from "lucide-react";

/* Radha — Module 3: Contract MIS + Subscription Lifecycle (self-contained preview)
   Seeded to match 003_contracts.sql. headroom = ACV - invoiced. */

const C = {
  primary: "#1B54F8", success: "#00A87A", warning: "#C8861A", danger: "#D63B2F",
  ai: "#6E44D4", aiBg: "#F5F3FF", bg: "#F3F2EE", surface: "#FFFFFF",
  border: "#E4E3DE", text: "#08090C", muted: "#7A7975",
};
const mono = "'DM Mono', ui-monospace, Menlo, monospace";
const sans = "'Inter', system-ui, sans-serif";

function formatINR(n) {
  if (n == null || isNaN(n)) return "—";
  const a = Math.abs(n), s = n < 0 ? "-" : "";
  if (a >= 1e7) return `${s}₹${(a / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${s}₹${Math.round(a / 1e5)}L`;
  return `${s}₹${a.toLocaleString("en-IN")}`;
}
const cr = (n) => +(n / 1e7).toFixed(2);

/* Seed: 8 contracts from 003_contracts.sql (amounts in ₹) */
const CONTRACTS = [
  { num: "CTR-2025-001", cust: "Reliance Industries Ltd", acv: 60000000, inv: 45000000, end: "2026-06-30", status: "active", auto: true },
  { num: "CTR-2025-002", cust: "Tata Consultancy Services", acv: 36000000, inv: 30000000, end: "2026-07-15", status: "active", auto: false },
  { num: "CTR-2024-014", cust: "Infosys Ltd", acv: 24000000, inv: 22000000, end: "2026-06-25", status: "expiring", auto: false },
  { num: "CTR-2024-021", cust: "Mahindra & Mahindra", acv: 18000000, inv: 17500000, end: "2026-06-15", status: "expiring", auto: false },
  { num: "CTR-2025-007", cust: "Wipro Ltd", acv: 15000000, inv: 6000000, end: "2026-08-31", status: "active", auto: true },
  { num: "CTR-2024-030", cust: "L&T Finance Holdings", acv: 42000000, inv: 41000000, end: "2026-06-28", status: "expiring", auto: false },
  { num: "CTR-2025-011", cust: "Bajaj Finance Ltd", acv: 28000000, inv: 12000000, end: "2026-09-30", status: "active", auto: false },
  { num: "CTR-2025-015", cust: "HDFC Bank Ltd", acv: 50000000, inv: 8000000, end: "2026-12-31", status: "active", auto: true },
].map(c => ({ ...c, headroom: c.acv - c.inv, short: c.cust.split(" ")[0] }));

const SUBS = [
  { cust: "Reliance Industries Ltd", plan: "Enterprise", mrr: 5000000, seats: 250, status: "active", upsell: true },
  { cust: "Tata Consultancy Services", plan: "Enterprise", mrr: 3000000, seats: 180, status: "active", upsell: false },
  { cust: "Wipro Ltd", plan: "Growth", mrr: 1250000, seats: 90, status: "upsell_pending", upsell: true },
  { cust: "Bajaj Finance Ltd", plan: "Growth", mrr: 2333333, seats: 120, status: "active", upsell: false },
  { cust: "Mahindra & Mahindra", plan: "Standard", mrr: 1500000, seats: 60, status: "paused", upsell: false },
];

const PROPOSAL = {
  cust: "Infosys Ltd", kind: "renewal", conf: 0.74, currentAcv: 24000000, proposedAcv: 27000000, uplift: 12.5, churn: 0.38, dte: 18,
  summary: "Contract expires in under 60 days and does not auto-renew; usage is up year-on-year, churn probability moderate (0.38). A high-risk termination-for-convenience clause adds urgency.",
  action: "Initiate renewal at a 12.5% uplift (₹2.7Cr ACV) and schedule a CS check-in before the termination-for-convenience window.",
};

const MODULES = [
  { code: "M1", name: "Collections Copilot", icon: PhoneCall },
  { code: "M2", name: "Dispute Resolution", icon: Scale },
  { code: "M3", name: "Contract MIS", icon: FileText, active: true },
  { code: "M4", name: "Revenue Forecast", icon: TrendingUp },
  { code: "M5", name: "Invoicing Engine", icon: Receipt },
  { code: "M6", name: "Payment Reminders", icon: Send },
  { code: "M7", name: "Cash Application", icon: Banknote },
  { code: "M8", name: "Cash Flow Forecast", icon: Gauge },
  { code: "M9", name: "CRM Pipeline", icon: LayoutGrid },
];

function Badge({ children, color }) {
  return <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase", color, background: `${color}14`, border: `0.5px solid ${color}33`, borderRadius: 20, padding: "3px 8px", whiteSpace: "nowrap" }}>{children}</span>;
}
function KPICard({ label, value, sub, accent }) {
  return <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", flex: 1, minWidth: 150 }}>
    <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>{label}</div>
    <div style={{ fontFamily: mono, fontSize: 26, fontWeight: 500, color: accent || C.text, marginTop: 8 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>
  </div>;
}

export default function Contracts() {
  const [view, setView] = useState("data");
  const [drawer, setDrawer] = useState(false);
  const [query, setQuery] = useState("");

  const rows = useMemo(() => CONTRACTS.filter(c => c.cust.toLowerCase().includes(query.toLowerCase())), [query]);
  const kpis = useMemo(() => ({
    acv: CONTRACTS.reduce((s, c) => s + c.acv, 0),
    headroom: CONTRACTS.reduce((s, c) => s + c.headroom, 0),
    expiring: CONTRACTS.filter(c => c.status === "expiring").length,
    upsell: SUBS.filter(s => s.upsell).length,
  }), []);
  const chartData = useMemo(() => CONTRACTS.map(c => ({ name: c.short, Invoiced: cr(c.inv), Headroom: cr(c.headroom) })), []);

  return (
    <div style={{ display: "flex", minHeight: 640, background: C.bg, fontFamily: sans, color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        .rrow:hover{background:${C.bg}!important}.nav:hover{background:#ffffff14}`}</style>

      <aside style={{ width: 224, background: "#0E1118", color: "#E7E9EE", padding: "20px 12px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 18px" }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: C.ai, display: "grid", placeItems: "center", fontFamily: mono, fontWeight: 600 }}>र</div>
          <div style={{ fontWeight: 600 }}>Radha</div>
        </div>
        {MODULES.map(m => { const Icon = m.icon; return (
          <div key={m.code} className="nav" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, marginBottom: 2, cursor: "pointer", background: m.active ? C.primary : "transparent", color: m.active ? "#fff" : "#AEB4C0" }}>
            <Icon size={15} /><span style={{ fontSize: 12.5 }}>{m.name}</span>
            <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 8, opacity: 0.6 }}>{m.code}</span>
          </div>); })}
        <div style={{ marginTop: 18, padding: "0 8px", fontSize: 10, color: "#5A6172" }}>Bharat Industries Ltd</div>
      </aside>

      <main style={{ flex: 1, padding: "22px 26px", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, letterSpacing: 0.6 }}>M3 · CONTRACT MIS + SUBSCRIPTION LIFECYCLE</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: "4px 0 0" }}>Contracts & Headroom</h1>
          </div>
          <div style={{ display: "flex", gap: 4, background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
            {["data", "loading", "empty", "error"].map(s => (
              <button key={s} onClick={() => setView(s)} style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, border: "none", borderRadius: 7, padding: "5px 9px", cursor: "pointer", background: view === s ? C.text : "transparent", color: view === s ? "#fff" : C.muted }}>{s}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
          <KPICard label="Total ACV" value={formatINR(kpis.acv)} sub="across 8 contracts" />
          <KPICard label="Live Headroom" value={formatINR(kpis.headroom)} sub="ACV − invoiced" accent={C.primary} />
          <KPICard label="Expiring < 60d" value={kpis.expiring} sub="need renewal motion" accent={C.warning} />
          <KPICard label="Upsell Signals" value={kpis.upsell} sub="subscriptions flagged" accent={C.success} />
        </div>

        <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 540px", minWidth: 320 }}>
            {/* EF-CORE renewal panel */}
            <div style={{ background: C.aiBg, border: `0.5px solid ${C.ai}33`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={14} color={C.ai} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8, color: C.ai }}>EF-CORE · RENEWAL PROPOSAL</span>
                <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 9, color: C.ai }}>confidence {PROPOSAL.conf}</span>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{PROPOSAL.cust}</span>
                <span style={{ fontFamily: mono, fontSize: 12, color: C.muted }}>{formatINR(PROPOSAL.currentAcv)}</span>
                <ArrowUpRight size={14} color={C.success} />
                <span style={{ fontFamily: mono, fontSize: 13, color: C.success, fontWeight: 500 }}>{formatINR(PROPOSAL.proposedAcv)}</span>
                <Badge color={C.success}>+{PROPOSAL.uplift}% uplift</Badge>
              </div>
              <p style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.5, color: "#2A2A2E" }}>{PROPOSAL.summary}</p>
              <button onClick={() => setDrawer(true)} style={{ marginTop: 12, background: C.ai, color: "#fff", border: "none", borderRadius: 8, padding: "7px 13px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Review renewal</button>
            </div>

            {/* Headroom bars */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "14px 14px 8px", marginBottom: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, marginBottom: 8 }}>Invoiced vs headroom (₹ Cr) per contract</div>
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke={C.border} vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: C.muted, fontFamily: mono }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10, fill: C.muted, fontFamily: mono }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => `₹${v}Cr`} contentStyle={{ borderRadius: 8, border: `0.5px solid ${C.border}`, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11, fontFamily: mono }} />
                  <Bar dataKey="Invoiced" stackId="a" fill={C.muted} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Headroom" stackId="a" fill={C.primary} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Contracts table */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderBottom: `0.5px solid ${C.border}` }}>
                <Search size={14} color={C.muted} />
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Filter contracts…" style={{ border: "none", outline: "none", fontSize: 13, flex: 1, background: "transparent", color: C.text }} />
                <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>{rows.length} contracts</span>
              </div>
              {view === "loading" && <Skeleton />}
              {view === "error" && <ErrorState />}
              {view === "empty" && <EmptyState />}
              {view === "data" && (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>{["Contract / Customer", "ACV", "Invoiced", "Headroom", "Expiry"].map((h, i) => (
                    <th key={h} style={{ textAlign: i >= 1 && i <= 3 ? "right" : "left", fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, fontWeight: 500, padding: "10px 14px", borderBottom: `0.5px solid ${C.border}` }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>
                    {rows.map((c, i) => {
                      const expSoon = c.status === "expiring";
                      return (
                        <tr key={i} className="rrow" onClick={() => setDrawer(true)} style={{ cursor: "pointer", borderBottom: `0.5px solid ${C.border}` }}>
                          <td style={{ padding: "11px 14px" }}>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>{c.cust}</div>
                            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 3 }}>
                              <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>{c.num}</span>
                              {c.auto && <Badge color={C.success}>auto-renew</Badge>}
                            </div>
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "right", fontFamily: mono, fontSize: 13 }}>{formatINR(c.acv)}</td>
                          <td style={{ padding: "11px 14px", textAlign: "right", fontFamily: mono, fontSize: 13, color: C.muted }}>{formatINR(c.inv)}</td>
                          <td style={{ padding: "11px 14px", textAlign: "right", fontFamily: mono, fontSize: 13, color: C.primary, fontWeight: 500 }}>{formatINR(c.headroom)}</td>
                          <td style={{ padding: "11px 14px" }}>
                            {expSoon ? <Badge color={C.warning}>{c.end.slice(5)}</Badge> : <span style={{ fontFamily: mono, fontSize: 12, color: C.muted }}>{c.end.slice(0, 7)}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Subscription / upsell rail */}
          <div style={{ flex: "0 0 250px", minWidth: 210 }}>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                <Repeat size={13} color={C.ai} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Subscriptions · MRR</span>
              </div>
              {SUBS.map((s, i) => (
                <div key={i} style={{ marginTop: 12, paddingTop: i ? 12 : 0, borderTop: i ? `0.5px solid ${C.border}` : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12.5, fontWeight: 500 }}>{s.cust.split(" ")[0]}</span>
                    {s.upsell ? <Badge color={C.success}>upsell</Badge> : <span style={{ fontFamily: mono, fontSize: 9, color: C.muted }}>{s.status}</span>}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    <span style={{ fontSize: 11, color: C.muted }}>{s.plan} · {s.seats} seats</span>
                    <span style={{ fontFamily: mono, fontSize: 11 }}>{formatINR(s.mrr)}/mo</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Governance</div>
              {[["Headroom = ACV − invoiced", "computed at DB layer"], ["MCA contract governance", "signed-on + audit trail"], ["Clause risk extraction", "1 high-risk flagged"]].map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 12 }}>
                  <ShieldCheck size={15} color={C.success} style={{ marginTop: 1, flexShrink: 0 }} />
                  <div><div style={{ fontSize: 12.5, fontWeight: 500 }}>{r[0]}</div><div style={{ fontSize: 11, color: C.muted }}>{r[1]}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {drawer && <RenewalDrawer onClose={() => setDrawer(false)} />}
    </div>
  );
}

function Skeleton() { return <div style={{ padding: 14 }}>{[...Array(5)].map((_, i) => <div key={i} style={{ height: 18, background: C.border, borderRadius: 6, marginBottom: 14, opacity: 0.6, animation: "pulse 1.4s ease-in-out infinite" }} />)}<style>{`@keyframes pulse{0%,100%{opacity:.35}50%{opacity:.7}}`}</style></div>; }
function ErrorState() { return <div style={{ padding: 40, textAlign: "center" }}><AlertTriangle size={26} color={C.danger} /><div style={{ marginTop: 10, fontWeight: 500 }}>Couldn’t load contracts</div><div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>The contracts service did not respond.</div><button style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}><RefreshCw size={13} /> Retry</button></div>; }
function EmptyState() { return <div style={{ padding: 44, textAlign: "center" }}><Inbox size={26} color={C.muted} /><div style={{ marginTop: 10, fontWeight: 500 }}>No contracts yet</div><div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Add or import contracts to track headroom.</div></div>; }

function RenewalDrawer({ onClose }) {
  const [decided, setDecided] = useState(null);
  const P = PROPOSAL;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "#08090C55", display: "flex", justifyContent: "flex-end", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 420, background: C.surface, height: "100%", padding: 22, overflow: "auto", boxShadow: "-12px 0 40px #0003" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: mono, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Renewal proposal</div>
          <X size={18} color={C.muted} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>
        <h3 style={{ margin: "8px 0 2px", fontSize: 17 }}>{P.cust}</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <CalendarClock size={14} color={C.warning} />
          <span style={{ fontFamily: mono, fontSize: 12, color: C.muted }}>expires in {P.dte} days · churn {P.churn}</span>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <div style={{ flex: 1, background: C.bg, borderRadius: 10, padding: 12 }}>
            <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", color: C.muted }}>Current ACV</div>
            <div style={{ fontFamily: mono, fontSize: 18, marginTop: 4 }}>{formatINR(P.currentAcv)}</div>
          </div>
          <div style={{ flex: 1, background: "#E9F7F1", borderRadius: 10, padding: 12 }}>
            <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", color: C.success }}>Proposed ACV</div>
            <div style={{ fontFamily: mono, fontSize: 18, marginTop: 4, color: C.success }}>{formatINR(P.proposedAcv)}</div>
            <div style={{ fontFamily: mono, fontSize: 10, color: C.success }}>+{P.uplift}% uplift</div>
          </div>
        </div>

        <div style={{ marginTop: 16, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Signal</div>
        <p style={{ fontSize: 13, lineHeight: 1.55, marginTop: 6 }}>{P.summary}</p>
        <div style={{ marginTop: 12, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Recommended action</div>
        <div style={{ background: C.aiBg, border: `0.5px solid ${C.ai}33`, borderRadius: 8, padding: 12, marginTop: 6, fontSize: 12.5, lineHeight: 1.5, color: "#2A2A2E" }}>{P.action}</div>

        {decided ? (
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8, background: decided === "approved" ? "#E9F7F1" : "#FBE9E7", border: `0.5px solid ${(decided === "approved" ? C.success : C.danger)}33`, borderRadius: 9, padding: 12, fontSize: 13, color: decided === "approved" ? C.success : C.danger }}>
            {decided === "approved" ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
            Renewal {decided}. Written to the audit log.
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
            <button onClick={() => setDecided("approved")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: C.success, color: "#fff", border: "none", borderRadius: 9, padding: "11px 0", fontSize: 13, fontWeight: 500, cursor: "pointer" }}><CheckCircle2 size={14} /> Approve renewal</button>
            <button onClick={() => setDecided("rejected")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "transparent", color: C.danger, border: `0.5px solid ${C.danger}55`, borderRadius: 9, padding: "11px 0", fontSize: 13, cursor: "pointer" }}><XCircle size={14} /> Reject</button>
          </div>
        )}
      </div>
    </div>
  );
}
